import * as XLSX from 'xlsx';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { Directive, Input, OnDestroy, Output } from '@angular/core';
import { UnRx } from '@pebula/utils';
import { PblNgridComponent, PblNgridConfigService, PblColumn, PblNgridPluginController } from '@pebula/ngrid';

import { SgTableExportManagerService, ExportInstance } from './export-manager.service';

declare module '@pebula/ngrid/core' {
  interface PblNgridConfig {
    excelExport?: {
      defaultFilename?: string;
      /**
       * Provides a way to manipulate the filename.
       * The provided parameter is the filename as set by the user, or the default filename if not.
       * Note that the filename DOES NOT include the extension (e.g. myFile and not myFile.xlsx)
       */
      fileNamePipe?: (fileName: string) => string;
    };
  }
}

const TABLE_TO_EXPORT_MAP = new WeakMap<PblNgridComponent<any>, SgExcelExportPluginDirective>();
const DEFAULT_FILENAME = 'excel-export';

/**
 * Returns true if the table is attached to an SgTableStickyPluginDirective instance
 */
export function getExcelExportPlugin(table: PblNgridComponent<any>): SgExcelExportPluginDirective | undefined {
  return TABLE_TO_EXPORT_MAP.get(table);
}

export interface ExcelExportAdapter<T = any, TRow = any> {
  context?: T;
  exclude?: string[];
  columns?: PblColumn[];
  data?: Observable<TRow[]>;
  fileName?: string | (() => string);
}

@Directive({
  selector: 'pbl-ngrid[excelExport]',
  standalone: false,
})
@UnRx()
export class SgExcelExportPluginDirective implements OnDestroy {
  _adapter: ExcelExportAdapter;
  @Input('excelExport') public set adapter(value: ExcelExportAdapter | '') {
    this._adapter = !value || (typeof value === 'string' && value === '') ? ({} as ExcelExportAdapter) : value;
  }
  public get adapter(): ExcelExportAdapter {
    return this._adapter;
  }

  @Output() canExport: BehaviorSubject<boolean>;
  @Output() exporting = new BehaviorSubject<boolean>(false);

  exportlimit = 10_000;
  private exportProcess: ExportInstance;

  isCanExportValid(tableDatasource = this.table?.ds) {
    return tableDatasource?.length > 0 && tableDatasource?.length < this.exportlimit;
  }

  constructor(
    pluginCtrl: PblNgridPluginController,
    protected exportManager: SgTableExportManagerService,
    protected readonly table: PblNgridComponent<any>,
    private config: PblNgridConfigService
  ) {
    TABLE_TO_EXPORT_MAP.set(table, this);

    this.canExport = new BehaviorSubject<boolean>(this.isCanExportValid(table.ds));

    pluginCtrl.events.pipe(UnRx(this, 'canExport')).subscribe((event) => {
      if (event.kind === 'onDataSource') {
        UnRx.kill(this, 'canExport');
        event.curr.onSourceChanged.subscribe(() => this.updateCanExport());
      }
    });
  }

  export(): void {
    const adapter = this.adapter;
    const simpleTransformer = (c: PblColumn, r: any) => [c.getValue(r)];

    let cols = adapter.columns || this.table.columnApi.visibleColumns;
    if (adapter.exclude) {
      cols = cols.filter((c) => adapter.exclude.indexOf(c.orgProp) === -1);
    }

    const inputData = adapter.data || of(this.table.ds.source);

    this.exporting.next(true);
    this.exportProcess = this.exportManager.addManagedExport(this.table, inputData.pipe(UnRx(this)));
    this.exportProcess.completed.subscribe(() => {
      if (this.exportProcess.state === 'complete') {
        const data: any[] = [];
        for (const row of this.exportProcess.data) {
          const rowData = [];
          for (const col of cols) {
            rowData.push(simpleTransformer(col, row));
          }
          data.push(rowData);
        }

        data.unshift(cols.map((c) => c.label));

        const { defaultFilename, fileNamePipe } = this.config.get('excelExport') || ({} as any);
        const adapterFileName = typeof adapter.fileName === 'function' ? adapter.fileName() : adapter.fileName;
        const match = /(?:(.+)(?:\.xlsx)$|(.+))/.exec(adapterFileName || defaultFilename || DEFAULT_FILENAME);

        let fileName = match[1] || match[2];
        if (fileNamePipe) {
          fileName = fileNamePipe(fileName);
        }
        fileName += '.xlsx';

        import('xlsx').then((xlsx) => {
          const ws: XLSX.WorkSheet = xlsx.utils.aoa_to_sheet(data);
          const wb: XLSX.WorkBook = xlsx.utils.book_new();
          xlsx.utils.book_append_sheet(wb, ws, 'Sheet1');
          xlsx.writeFile(wb, fileName);
        });
        this.exporting.next(false);
        this.updateCanExport();
      } else {
        this.exporting.next(false);
        this.updateCanExport();
      }
    });
    this.updateCanExport();
  }

  ngOnDestroy(): void {
    TABLE_TO_EXPORT_MAP.delete(this.table);
  }

  private updateCanExport(): void {
    const processRunning = this.exportProcess ? this.exportProcess.isRunning : false;
    this.canExport.next(this.isCanExportValid() && !processRunning);
  }
}
